import React, { useState } from 'react';
import axios from 'axios';
import SEO from '../components/SEO';
import { Mail, MessageSquare, Send, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const Contact = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: t.catGeneral,
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      await axios.post('/api/contact', {
        firstName,
        lastName,
        email: formData.email,
        message: `[Subject: ${formData.subject}]\n\n${formData.message}`
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to send message:", err);
      alert(t.error + " Failed to send message. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title={`${t.contact} - PlataPost`}
        description={t.contactHeroSubtitle}
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left Column: Info */}
          <div className="space-y-12">
            <div className="space-y-4">
              <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">
                {t.contactConnect}
              </p>
              <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">
                {t.contactLetsConnect.split(' ')[0]} <span className="text-indigo-500">{t.contactLetsConnect.split(' ').slice(1).join(' ')}</span>
              </h1>
              <p className="text-xl text-gray-400 font-medium max-w-xl">
                {t.contactHeroSubtitle}
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-6 p-8 rounded-[2.5rem] glass-card border border-white/5 group shadow-2xl shadow-indigo-600/5">
                <div className="w-14 h-14 bg-indigo-500/10 rounded-2xl flex items-center justify-center border border-indigo-500/20 group-hover:scale-110 transition-transform">
                  <Mail className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-white tracking-tight mb-1">{t.emailUs}</h3>
                  <p className="text-gray-500 text-sm mb-4 font-medium">{t.generalInquiries}</p>
                  <a href="mailto:support@platapost.com" className="text-indigo-400 font-black hover:text-indigo-300 transition-colors">support@platapost.com</a>
                </div>
              </div>

              <div className="flex items-start gap-6 p-8 rounded-[2.5rem] glass-card border border-white/5 group shadow-2xl shadow-indigo-600/5">
                <div className="w-14 h-14 bg-indigo-500/10 rounded-2xl flex items-center justify-center border border-indigo-500/20 group-hover:scale-110 transition-transform">
                  <MessageSquare className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-white tracking-tight mb-1">{t.followUs}</h3>
                  <p className="text-gray-500 text-sm mb-4 font-medium">{t.getUpdates}</p>
                  <div className="flex gap-6">
                    <a href="#" className="text-gray-400 hover:text-indigo-400 font-black uppercase text-xs tracking-widest">Instagram</a>
                    <a href="#" className="text-gray-400 hover:text-indigo-400 font-black uppercase text-xs tracking-widest">LinkedIn</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="glass-card rounded-[3.5rem] p-10 lg:p-14 border border-white/10 shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/10 blur-[80px] -mr-32 -mt-32" />
            
            {submitted ? (
              <div className="text-center py-20 relative z-10">
                <div className="w-24 h-24 bg-green-500/10 rounded-[2rem] flex items-center justify-center mx-auto mb-8 border border-green-500/20">
                  <CheckCircle2 className="w-12 h-12 text-green-400" />
                </div>
                <h2 className="text-3xl font-black text-white mb-4 tracking-tighter">{t.messageSent}</h2>
                <p className="text-gray-400 mb-10 text-lg font-medium">{t.thanksForReaching}</p>
                <button 
                  onClick={() => setSubmitted(false)}
                  className="px-8 py-4 bg-white/5 text-white rounded-2xl font-black hover:bg-white/10 transition-all text-sm uppercase tracking-widest border border-white/10"
                >
                  {t.sendAnother}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{t.fullName}</label>
                    <input 
                      required
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all placeholder:text-gray-700 text-white font-bold"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{t.emailAddress}</label>
                    <input 
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all placeholder:text-gray-700 text-white font-bold"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{t.subject}</label>
                  <select 
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-6 py-4 rounded-2xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all text-white font-black uppercase text-xs tracking-widest appearance-none"
                  >
                    <option className="bg-gray-900" value={t.catGeneral}>{t.catGeneral}</option>
                    <option className="bg-gray-900" value="Sales Inquiry">Sales Inquiry</option>
                    <option className="bg-gray-900" value="Demo Request">Demo Request</option>
                    <option className="bg-gray-900" value={t.catPricingSupport}>{t.catPricingSupport}</option>
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">{t.message}</label>
                  <textarea 
                    required
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full px-6 py-4 rounded-3xl bg-white/5 border border-white/5 focus:bg-white/10 focus:ring-2 focus:ring-indigo-600 outline-none transition-all placeholder:text-gray-700 text-white font-medium leading-relaxed resize-none"
                    placeholder={t.tellUsHelp}
                  />
                </div>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-600/20 flex items-center justify-center gap-3 group disabled:opacity-50"
                >
                  <span>{isSubmitting ? t.sending : t.sendMessage}</span>
                  <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
